import { useCallback, useEffect, useState } from 'react';
import { XaPhuong, XaPhuongInput, XaPhuongResponse } from '@/types/schemas/xa-phuong.type';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UseWardReturn {
  wards: XaPhuong[];
  isLoading: boolean;
  addWard: (newWard: XaPhuongInput) => Promise<void>;
  updateWard: (uuid: string, updatedWard: XaPhuongInput) => Promise<void>;
  deleteWard: (uuid: string) => Promise<void>;
  refreshWards: () => Promise<void>;
  getWardsByProvince: (provinceId: string) => Promise<XaPhuong[]>;
  getWardsByDistrict: (districtId: string) => Promise<XaPhuong[]>;
}

export const useWard = (initialWards: XaPhuong[] = []): UseWardReturn => {
  const [wards, setWards] = useState<XaPhuong[]>(initialWards);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchWards = useCallback(async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      console.log('Fetching wards for entity:', entity.slug);
      const response = await api.get<XaPhuongResponse>(`/entities/${entity.slug}/erp/wards/`);
      console.log('API response for wards:', response.data);
      setWards(response.data.results || []);
    } catch (error) {
      console.error('Error fetching wards:', error);
    } finally {
      setIsLoading(false);
    }
  }, [entity?.slug]);

  const getWardsByProvince = async (provinceId: string): Promise<XaPhuong[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<XaPhuongResponse>(
        `/entities/${entity.slug}/erp/wards/by_province/?ma_tinh=${provinceId}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching wards by province:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const getWardsByDistrict = async (districtId: string): Promise<XaPhuong[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<XaPhuongResponse>(
        `/entities/${entity.slug}/erp/wards/by_district/?ma_quan=${districtId}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching wards by district:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const addWard = async (newWard: XaPhuongInput) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.post(`/entities/${entity.slug}/erp/wards/`, {
        ma_xp: newWard.ma_xp,
        ten_xp: newWard.ten_xp,
        ten_xp2: newWard.ten_xp2 || null,
        ma_tinh: newWard.ma_tinh || null,
        ma_quan: newWard.ma_quan || null,
        status: newWard.status || '1'
      });

      const addedWard: XaPhuong = response.data;
      setWards(prev => [...prev, addedWard]);
    } catch (error) {
      console.error('Error adding ward:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateWard = async (uuid: string, updatedWard: XaPhuongInput) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.put(`/entities/${entity.slug}/erp/wards/${uuid}/`, {
        ma_xp: updatedWard.ma_xp,
        ten_xp: updatedWard.ten_xp,
        ten_xp2: updatedWard.ten_xp2 || null,
        ma_tinh: updatedWard.ma_tinh || null,
        ma_quan: updatedWard.ma_quan || null,
        status: updatedWard.status || '1'
      });

      // Update the ward in the list
      const updatedWardData: XaPhuong = response.data;

      setWards(prev => prev.map(ward => (ward.uuid === updatedWardData.uuid ? updatedWardData : ward)));
    } catch (error) {
      console.error('Error updating ward:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteWard = async (uuid: string) => {
    if (!entity?.slug) return;

    console.log('Attempting to delete ward with UUID:', uuid);

    setIsLoading(true);
    try {
      console.log('Sending DELETE request to:', `/entities/${entity.slug}/erp/wards/${uuid}/`);
      await api.delete(`/entities/${entity.slug}/erp/wards/${uuid}/`);

      console.log('Delete request successful, updating state');
      // Remove the ward from the list
      setWards(prev => {
        console.log('Current wards:', prev);
        console.log('Filtering out ward with UUID:', uuid);
        return prev.filter(ward => ward.uuid !== uuid);
      });
    } catch (error) {
      console.error('Error deleting ward:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    console.log('useWard hook: entity slug changed, fetching wards...', entity?.slug);
    fetchWards();
  }, [fetchWards, entity?.slug]);

  return {
    wards,
    isLoading,
    addWard,
    updateWard,
    deleteWard,
    refreshWards: fetchWards,
    getWardsByProvince,
    getWardsByDistrict
  };
};
